import { z } from 'zod';
import { IsoDateTimeSchema } from './common.js';
export const VideoWallModeSchema = z.enum(['extend', 'mirror']);
export const VideoWallPublicationStatusSchema = z.enum(['slicing', 'ready', 'failed', 'stopped']);
export const CreateVideoWallRequestSchema = z.object({
    name: z.string().min(1).max(200),
    rows: z.number().int().min(1).max(16),
    cols: z.number().int().min(1).max(16),
    mode: VideoWallModeSchema.default('extend'),
    panelWidthMm: z.number().positive().nullable().optional(),
    panelHeightMm: z.number().positive().nullable().optional(),
    bezelHorizontalMm: z.number().min(0).default(0),
    bezelVerticalMm: z.number().min(0).default(0),
});
export const UpdateVideoWallRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    mode: VideoWallModeSchema.optional(),
    panelWidthMm: z.number().positive().nullable().optional(),
    panelHeightMm: z.number().positive().nullable().optional(),
    bezelHorizontalMm: z.number().min(0).optional(),
    bezelVerticalMm: z.number().min(0).optional(),
})
    .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field is required',
});
export const AssignVideoWallTileRequestSchema = z.object({
    screenId: z.string().min(1),
    row: z.number().int().min(0),
    col: z.number().int().min(0),
});
export const PublishVideoWallRequestSchema = z.object({
    mediaAssetId: z.string().min(1),
});
export const VideoWallTileSchema = z.object({
    row: z.number().int().nonnegative(),
    col: z.number().int().nonnegative(),
    screenId: z.string().min(1),
});
export const VideoWallPublicationSchema = z.object({
    id: z.string().min(1),
    videoWallId: z.string().min(1),
    sourceMediaAssetId: z.string().min(1),
    timelineId: z.string().min(1),
    status: VideoWallPublicationStatusSchema,
    epochStartAt: IsoDateTimeSchema.nullable(),
    errorMessage: z.string().nullable(),
    readyScreenCount: z.number().int().nonnegative(),
    assignedScreenCount: z.number().int().nonnegative(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
/**
 * Server-computed operational state (item 02). The backend decides the
 * official wall and tile state; the dashboard only formats it.
 */
export const VideoWallOperationalStateSchema = z.enum([
    'no_content',
    /** Content is published, but the wall has no screens assigned to any tile. */
    'no_screens',
    'slicing',
    'failed',
    'waiting_for_tiles',
    'starting',
    'on_air',
    'interrupted',
    'stopped',
]);
/** From the current device's heartbeat. Missing or invalid data is `unknown`, never `offline`. */
export const VideoWallTileConnectionSchema = z.enum(['online', 'offline', 'unknown']);
/** Why a tile is keeping the wall from going, or staying, live. */
export const VideoWallTileBlockingReasonSchema = z.enum([
    'no_device',
    'offline',
    'connection_unknown',
    'not_ready',
    'not_playing',
]);
export const VideoWallTileStatusSchema = VideoWallTileSchema.extend({
    /** Current device: the active device linked to the screen with the latest heartbeat. */
    deviceId: z.string().min(1).nullable(),
    /** More than one active device is linked to this screen. */
    multipleDevicesLinked: z.boolean(),
    connection: VideoWallTileConnectionSchema,
    lastHeartbeatAt: IsoDateTimeSchema.nullable(),
    /** READY for the active publication's timeline, reported by the current device. */
    ready: z.boolean(),
    /** First frame reported on screen for the current device and timeline. */
    playing: z.boolean(),
    /** Configured from tile position (row 0, col 0 leads); not reported by the player. */
    role: z.enum(['leader', 'follower']),
    muted: z.boolean(),
    blockingReason: VideoWallTileBlockingReasonSchema.nullable(),
    /** Seconds waiting after the epoch; null when not waiting for a first frame. */
    waitingForFrameSeconds: z.number().int().nonnegative().nullable(),
    /** Waiting for a first frame for at least 60 seconds; display only. */
    stuck: z.boolean(),
});
export const VideoWallOperationsSchema = z.object({
    state: VideoWallOperationalStateSchema,
    requiredTileCount: z.number().int().nonnegative(),
    readyTileCount: z.number().int().nonnegative(),
    onlineTileCount: z.number().int().nonnegative(),
    /** Live and READY tiles, including tiles waiting for their first frame. */
    liveReadyTileCount: z.number().int().nonnegative(),
    /** Tiles that reported their first frame for the current timeline. */
    playingTileCount: z.number().int().nonnegative(),
    /** False when the (0,0) tile is empty, so the wall has no audio leader. */
    hasLeader: z.boolean(),
    /** No drift telemetry exists yet: cloud READY gating only (ADR 0007). */
    drift: z.literal('not_measured'),
    evaluatedAt: IsoDateTimeSchema,
});
export const VideoWallSchema = z.object({
    id: z.string().min(1),
    organisationId: z.string().min(1),
    name: z.string().min(1),
    rows: z.number().int().positive(),
    cols: z.number().int().positive(),
    mode: VideoWallModeSchema,
    panelWidthMm: z.number().positive().nullable(),
    panelHeightMm: z.number().positive().nullable(),
    bezelHorizontalMm: z.number().nonnegative(),
    bezelVerticalMm: z.number().nonnegative(),
    assignedTileCount: z.number().int().nonnegative(),
    activePublication: VideoWallPublicationSchema.nullable(),
    operations: VideoWallOperationsSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
export const VideoWallDetailSchema = VideoWallSchema.extend({
    tiles: z.array(VideoWallTileStatusSchema),
});
/**
 * Device-facing wall assignment (ADR 0006 additive / ADR 0007).
 * `item` is null while slicing; `epochStartAt` is null until READY quorum.
 */
export const DeviceWallAssignmentItemSchema = z.object({
    assetId: z.string().min(1),
    url: z.string().url(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    durationSeconds: z.number().positive(),
    mimeType: z.string().min(1),
});
export const DeviceWallAssignmentSchema = z.object({
    wallId: z.string().min(1),
    publicationId: z.string().min(1),
    /** The published source asset (item.assetId is per-tile). Optional for older servers. */
    sourceMediaAssetId: z.string().uuid().optional(),
    timelineId: z.string().min(1),
    mode: VideoWallModeSchema,
    rows: z.number().int().positive(),
    cols: z.number().int().positive(),
    row: z.number().int().nonnegative(),
    col: z.number().int().nonnegative(),
    isLeader: z.boolean(),
    muteAudio: z.boolean(),
    status: VideoWallPublicationStatusSchema,
    item: DeviceWallAssignmentItemSchema.nullable(),
    epochStartAt: IsoDateTimeSchema.nullable(),
});
export const DeviceWallReadyRequestSchema = z.object({
    publicationId: z.string().min(1),
    timelineId: z.string().min(1),
});
export const DeviceWallReadyResponseSchema = z.object({
    publicationId: z.string().min(1),
    timelineId: z.string().min(1),
    readyScreenCount: z.number().int().nonnegative(),
    assignedScreenCount: z.number().int().nonnegative(),
    epochStartAt: IsoDateTimeSchema.nullable(),
    allReady: z.boolean(),
});
/** Sent once per timeline when the tile's first frame is really on screen. */
export const DeviceWallPlayingRequestSchema = z.object({
    publicationId: z.string().min(1),
    timelineId: z.string().min(1),
});
export const DeviceWallPlayingResponseSchema = z.object({
    publicationId: z.string().min(1),
    timelineId: z.string().min(1),
    /** When the first report for this tile and timeline was stored (repeats keep it). */
    playingAt: IsoDateTimeSchema,
});
//# sourceMappingURL=video-wall.js.map